import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@follosoft/react-components";

import type { ContractContactsApiResponse } from "../types";

import { fetchAllPages } from "../utils/fetch-all-pages";
import { paramsSerializer } from "../utils/params-serializer";

import { CONTRACT_CONTACTS_ENDPOINT, CONTRACT_CONTACTS_PER_PAGE } from "./endpoints";
import { contractContactsQueryKey } from "./query-keys";

/**
 * Lists the contract's alert subscriptions from `GET /contract-contacts`.
 * Ordered by contact so a contact's alert types arrive together. The endpoint
 * paginates rows — at most 100 a page — while the table groups them by contact,
 * and one contact can hold up to 30 (10 numbers × call + SMS, 10 addresses); so
 * every page is fetched and the rows are grouped once they are all in (see
 * `contractContactGroups`). The attached contact is eager-loaded by the
 * backend, so no `include` is needed.
 */
export const useContractContactsService = (contractId: string | number | undefined) =>
  useQuery<ContractContactsApiResponse>({
    queryKey: contractContactsQueryKey(String(contractId ?? "")),
    queryFn: () =>
      fetchAllPages(async (page) => {
        const res = await axiosInstance().get<ContractContactsApiResponse>(CONTRACT_CONTACTS_ENDPOINT, {
          params: {
            // `id` breaks the ties: pages are separate queries, and OFFSET over
            // a non-unique key may repeat or skip a row at a page boundary.
            sort: "contact_id,id",
            per_page: CONTRACT_CONTACTS_PER_PAGE,
            page,
            contract_id: contractId,
          },
          paramsSerializer,
        });

        return res.data;
      }),
    enabled: Boolean(contractId),
    retry: false,
    refetchOnWindowFocus: false,
  });
